import React, { useEffect, useState } from "react";
import {
  Container,
  Typography,
  Card,
  CardContent,
  CardMedia,
  CircularProgress,
  Box,
  List,
  ListItem,
  Tabs,
  Tab,
  IconButton,
  TextField,
  Button,
  Select,
  MenuItem,
  Grid,
  Tooltip,
} from "@mui/material";
import { ArrowBack, Edit, Save, Cancel } from "@mui/icons-material";
import {
  doc,
  getDoc,
  collection,
  getDocs,
  updateDoc,
} from "firebase/firestore";
import { useParams, useNavigate } from "react-router-dom";
import { firestore } from "../firebase";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
} from "recharts";
import axios from "axios";

const DEFAULT_SHOP_IMAGE = "";

// Format Firestore timestamps
const formatDateTime = (timestamp) => {
  if (!timestamp) return "N/A";
  if (timestamp.toDate) {
    const date = timestamp.toDate();
    return `${date.getFullYear()}-${(date.getMonth() + 1)
      .toString()
      .padStart(2, "0")}-${date.getDate().toString().padStart(2, "0")} - ${date
      .getHours()
      .toString()
      .padStart(2, "0")}:${date.getMinutes().toString().padStart(2, "0")}`;
  }
  return "Invalid Date";
};

// Format log details
const formatLogDetails = (log) => {
  if (!log.before || !log.after) return log.action.replace("_", " ");

  let changedFields = Object.keys(log.after || {}).filter(
    (key) =>
      JSON.stringify(log.before[key] || "") !==
      JSON.stringify(log.after[key] || "")
  );

  if (changedFields.length === 0) return log.action.replace("_", " ");

  return (
    <List sx={{ pl: 2 }}>
      {changedFields.map((key) => {
        let beforeValue = log.before[key] || "";
        let afterValue = log.after[key] || "";

        // Convert object values to strings for display
        if (typeof beforeValue === "object" || typeof afterValue === "object") {
          beforeValue = JSON.stringify(beforeValue, null, 2);
          afterValue = JSON.stringify(afterValue, null, 2);
        }

        // Prevent crash by checking .length safely
        beforeValue =
          typeof beforeValue === "string"
            ? beforeValue
            : JSON.stringify(beforeValue);
        afterValue =
          typeof afterValue === "string"
            ? afterValue
            : JSON.stringify(afterValue);

        if (beforeValue.length > 50)
          beforeValue = beforeValue.substring(0, 50) + "...";
        if (afterValue.length > 50)
          afterValue = afterValue.substring(0, 50) + "...";

        return (
          <ListItem key={key} sx={{ fontSize: "14px", ml: 2 }}>
            <strong>{key}</strong>: "{beforeValue}" → "{afterValue}"
          </ListItem>
        );
      })}
    </List>
  );
};

const ShopDetailsPage = () => {
  const { shopId } = useParams();
  const navigate = useNavigate();
  const [tab, setTab] = useState(0);
  const [shop, setShop] = useState(null);
  const [vehicles, setVehicles] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [timespan, setTimespan] = useState("last28");
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");
  const [metrics, setMetrics] = useState({});
  const [editing, setEditing] = useState(false);
  const [editedShop, setEditedShop] = useState(null);
  const [editingVehicleId, setEditingVehicleId] = useState(null);
  const [vehicleFormData, setVehicleFormData] = useState(null);

  useEffect(() => {
    fetchShopDetails();
  }, [shopId, timespan]);

  const fetchShopDetails = async () => {
    try {
      const shopRef = doc(firestore, "rentalShops", shopId);
      const shopSnap = await getDoc(shopRef);
      if (!shopSnap.exists()) return;
      const shopData = shopSnap.data();
      setShop(shopData);
      setEditedShop(shopData);
      console.log("shopData.imageUrl: ", shopData.imageUrl);
      console.log("shopData.name: ", shopData.name);
      console.log("shopData: ", shopData);

      // Fetch Vehicles
      const vehicleSnap = await getDocs(collection(shopRef, "vehicles"));
      const fetchedVehicles = vehicleSnap.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setVehicles(fetchedVehicles);

      // Fetch Bookings
      const bookingsSnap = await getDocs(collection(shopRef, "bookings"));
      const bookingsData = bookingsSnap.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      // Filter bookings based on the selected timespan
      const filteredBookings = filterBookingsByTimespan(bookingsData);
      setBookings(filteredBookings);

      // Fetch Logs
      const logsSnap = await getDocs(collection(shopRef, "logs"));
      setLogs(logsSnap.docs.map((doc) => ({ id: doc.id, ...doc.data() })));

      // Calculate metrics
      calculateMetrics(filteredBookings, fetchedVehicles);
    } catch (error) {
      console.error("Error fetching shop details:", error);
    } finally {
      setLoading(false);
    }
  };

  // Function to update a specific time slot
  const updateOperatingHours = (day, index, field, value) => {
    const updatedHours = { ...editedShop.operatingHours };
    updatedHours[day][index][field] = value;
    setEditedShop({ ...editedShop, operatingHours: updatedHours });
  };

  // Function to add a new time slot for a day
  const addOperatingHour = (day) => {
    const updatedHours = { ...editedShop.operatingHours };
    if (!updatedHours[day]) updatedHours[day] = [];
    updatedHours[day].push({ open: "08:00", close: "18:00" }); // Default time range
    setEditedShop({ ...editedShop, operatingHours: updatedHours });
  };

  // Function to remove a time slot for a day
  const removeOperatingHour = (day, index) => {
    const updatedHours = { ...editedShop.operatingHours };
    updatedHours[day].splice(index, 1);
    setEditedShop({ ...editedShop, operatingHours: updatedHours });
  };

  const handleEditClick = () => {
    setEditing(true);
  };

  const handleCancelEdit = () => {
    setEditing(false);
    setEditedShop(shop);
  };

  const handleSaveEdit = async () => {
    try {
      const shopRef = doc(firestore, "rentalShops", shopId);
      await updateDoc(shopRef, editedShop);
      setShop(editedShop);
      setEditing(false);
    } catch (error) {
      console.error("Error updating shop:", error);
    }
  };

  const handleInputChange = (e) => {
    setEditedShop({ ...editedShop, [e.target.name]: e.target.value });
  };

  const filterBookingsByTimespan = (bookings) => {
    const now = new Date();
    let startDate, endDate;

    if (timespan === "last7") {
      startDate = new Date();
      startDate.setDate(now.getDate() - 7);
    } else if (timespan === "last28") {
      startDate = new Date();
      startDate.setDate(now.getDate() - 28);
    } else if (timespan === "thisYear") {
      startDate = new Date(now.getFullYear(), 0, 1);
    } else if (timespan === "custom" && customStart && customEnd) {
      startDate = new Date(customStart);
      endDate = new Date(customEnd);
    } else {
      return bookings;
    }

    endDate = endDate || now;
    return bookings.filter((booking) => {
      const bookingDate = new Date(booking.startDate);
      return bookingDate >= startDate && bookingDate <= endDate;
    });
  };

  const calculateMetrics = (bookings, vehicles) => {
    let onlineBookingsCount = 0;
    let manualBookingsCount = 0;
    let totalBookingLength = 0;
    let totalDailyRate = 0;
    let totalBookingPrice = 0;
    let totalAmount = 0;

    bookings.forEach((booking) => {
      if (booking.bookingMethod === "online") {
        onlineBookingsCount++;
        let start = new Date(booking.startDate);
        let end = new Date(booking.endDate);
        let days = (end - start) / (1000 * 60 * 60 * 24);
        totalBookingLength += days;
        totalDailyRate += booking.bill?.dailyRate;
        totalBookingPrice += days * booking.bill?.dailyRate;
        totalAmount += days * booking.bill?.dailyRate;
      } else {
        manualBookingsCount++;
      }
    });

    setMetrics({
      vehiclesCount: vehicles.length,
      bookingsCount: bookings.length,
      manualBookingsCount,
      onlineBookingsCount,
      avgBookingLength: (totalBookingLength / onlineBookingsCount || 0).toFixed(
        2
      ),
      avgDailyRate: (totalDailyRate / onlineBookingsCount || 0).toFixed(2),
      avgBookingPrice: (totalBookingPrice / onlineBookingsCount || 0).toFixed(
        2
      ),
      totalAmount: totalAmount.toFixed(2),
      payoutAmount: (totalAmount * 0.85).toFixed(2),
      commission: (totalAmount * 0.15).toFixed(2),
    });
  };

  const handleVehicleEditClick = (vehicle) => {
    setEditingVehicleId(vehicle.id);
    setVehicleFormData({ ...vehicle });
  };

  const handleVehicleCancelEdit = () => {
    setEditingVehicleId(null);
    setVehicleFormData(null);
  };

  const handleVehicleInputChange = (e) => {
    const { name, value } = e.target;
    setVehicleFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleVehicleSaveEdit = async () => {
    if (!vehicleFormData) return;

    try {
      const vehicleRef = doc(
        firestore,
        `rentalShops/${shopId}/vehicles/${vehicleFormData.id}`
      );

      // Store previous data for logging
      const before = vehicles.find((v) => v.id === vehicleFormData.id);

      // Update Firestore
      await updateDoc(vehicleRef, vehicleFormData);

      // Call Cloud Function to log update
      await axios.post("/logVehicleUpdate", {
        shopId,
        vehicleId: vehicleFormData.id,
        userId: "currentUserId", // Replace with actual user ID
        before,
        after: vehicleFormData,
      });

      // Update state
      setVehicles((prev) =>
        prev.map((v) => (v.id === vehicleFormData.id ? vehicleFormData : v))
      );
      setEditingVehicleId(null);
      setVehicleFormData(null);
    } catch (error) {
      console.error("Error updating vehicle:", error);
    }
  };

  return (
    <Container sx={{ mt: 4, mb: 4 }}>
      {loading ? (
        <CircularProgress sx={{ display: "block", margin: "20px auto" }} />
      ) : (
        <>
          {/* 🔹 Rental Shop Details Section */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box display="flex" justifyContent="space-between">
                <IconButton onClick={() => navigate(-1)}>
                  <ArrowBack />
                </IconButton>
                <Typography variant="h5">Shop Details</Typography>
                {!editing ? (
                  <IconButton onClick={handleEditClick}>
                    <Edit />
                  </IconButton>
                ) : (
                  <Box>
                    <IconButton onClick={handleSaveEdit} color="success">
                      <Save />
                    </IconButton>
                    <IconButton onClick={handleCancelEdit} color="error">
                      <Cancel />
                    </IconButton>
                  </Box>
                )}
              </Box>

              {!editing ? (
                <>
                  <CardMedia
                    component="img"
                    height="250"
                    image={shop.imageUrl || DEFAULT_SHOP_IMAGE}
                    alt={shop.name}
                    sx={{ borderRadius: 2, mb: 2 }}
                  />
                  <Typography variant="h6">{shop.name}</Typography>
                  <Typography>{shop.description}</Typography>
                  <Typography sx={{ mt: 1 }}>📍 {shop.fullAddress}</Typography>
                  <Typography sx={{ mt: 2 }}>🕒 Opening Hours:</Typography>
                  {shop.operatingHours ? (
                    Object.entries(shop.operatingHours).map(([day, slots]) => (
                      <Typography key={day}>
                        <strong>{day}:</strong>{" "}
                        {slots.length > 0
                          ? slots
                              .map((slot) => `${slot.open} - ${slot.close}`)
                              .join(", ")
                          : "Closed"}
                      </Typography>
                    ))
                  ) : (
                    <Typography>Opening hours not available</Typography>
                  )}
                </>
              ) : (
                <>
                  <TextField
                    label="Shop Name"
                    fullWidth
                    sx={{ mb: 2 }}
                    name="name"
                    value={editedShop.name}
                    onChange={handleInputChange}
                  />
                  <TextField
                    label="Description"
                    fullWidth
                    sx={{ mb: 2 }}
                    name="description"
                    value={editedShop.description}
                    onChange={handleInputChange}
                  />
                  <TextField
                    label="Address"
                    fullWidth
                    sx={{ mb: 2 }}
                    name="address"
                    value={editedShop.address}
                    onChange={handleInputChange}
                  />
                  <Typography sx={{ mt: 2, mb: 1 }}>
                    🕒 Edit Opening Hours:
                  </Typography>
                  {Object.entries(editedShop.operatingHours || {}).map(
                    ([day, slots]) => (
                      <Box key={day} sx={{ mb: 2 }}>
                        <Typography variant="h6">{day}</Typography>
                        {slots.map((slot, index) => (
                          <Box
                            display="flex"
                            alignItems="center"
                            key={`${day}-${index}`}
                            sx={{ mb: 1 }}
                          >
                            <TextField
                              label="Open"
                              type="time"
                              value={slot.open}
                              onChange={(e) =>
                                updateOperatingHours(
                                  day,
                                  index,
                                  "open",
                                  e.target.value
                                )
                              }
                              sx={{ mr: 1 }}
                            />
                            <TextField
                              label="Close"
                              type="time"
                              value={slot.close}
                              onChange={(e) =>
                                updateOperatingHours(
                                  day,
                                  index,
                                  "close",
                                  e.target.value
                                )
                              }
                              sx={{ mr: 1 }}
                            />
                            <IconButton
                              color="error"
                              onClick={() => removeOperatingHour(day, index)}
                            >
                              <Cancel />
                            </IconButton>
                          </Box>
                        ))}
                        <Button
                          variant="outlined"
                          onClick={() => addOperatingHour(day)}
                        >
                          + Add Time Slot
                        </Button>
                      </Box>
                    )
                  )}
                </>
              )}
            </CardContent>
          </Card>

          {/* 🔹 Navigation Tabs */}
          <Box display="flex" alignItems="center" mb={2}>
            <Tabs value={tab} onChange={(_, newTab) => setTab(newTab)}>
              <Tab label="Overview" />
              <Tab label="Vehicles" />
              <Tab label="Bookings" />
              <Tab label="Logs" />
            </Tabs>
          </Box>
          {tab === 0 && (
            <Box>
              <Typography variant="h5">Overview</Typography>
              <Box display="flex" alignItems="center" sx={{ mb: 2 }}>
                <Select
                  value={timespan}
                  onChange={(e) => setTimespan(e.target.value)}
                  sx={{ minWidth: 180, mr: 2 }}
                >
                  <MenuItem value="last7">Last 7 days</MenuItem>
                  <MenuItem value="last28">Last 28 days</MenuItem>
                  <MenuItem value="thisYear">This year</MenuItem>
                  <MenuItem value="custom">Custom range</MenuItem>
                </Select>
                {timespan === "custom" && (
                  <>
                    <TextField
                      type="date"
                      value={customStart}
                      onChange={(e) => setCustomStart(e.target.value)}
                      sx={{ mr: 2 }}
                    />
                    <TextField
                      type="date"
                      value={customEnd}
                      onChange={(e) => setCustomEnd(e.target.value)}
                      sx={{ mr: 2 }}
                    />
                    <Button variant="contained" onClick={fetchShopDetails}>
                      Show
                    </Button>
                  </>
                )}
              </Box>
              <Grid container spacing={2}>
                {Object.entries(metrics).map(([key, value]) => (
                  <Grid item xs={6} sm={4} key={key}>
                    <Card>
                      <CardContent>
                        <Typography variant="h6">
                          {key.replace(/([A-Z])/g, " $1")}
                        </Typography>
                        <Typography variant="h4">{value}</Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}

          {tab === 1 && (
            <Box>
              <Typography variant="h5">Vehicles</Typography>
              <Grid container spacing={2}>
                {vehicles.map((vehicle) => (
                  <Grid item xs={12} sm={6} md={4} key={vehicle.id}>
                    <Card
                      sx={{
                        display: "flex",
                        position: "relative",
                        flexDirection: "column",
                        alignItems: "center",
                        p: 2,
                      }}
                    >
                      {/* Edit Icon */}
                      <Box sx={{ position: "absolute", top: 8, right: 8 }}>
                        <IconButton
                          onClick={() => handleVehicleEditClick(vehicle)}
                          size="small"
                        >
                          <Edit />
                        </IconButton>
                      </Box>

                      {/* Vehicle Image */}
                      <CardMedia
                        component="img"
                        height="120"
                        image={
                          vehicle.images?.length
                            ? vehicle.images[0].url
                            : DEFAULT_SHOP_IMAGE
                        }
                        alt={`${vehicle.brand} ${vehicle.model}`}
                        sx={{
                          borderRadius: 2,
                          objectFit: "cover",
                          width: "100%",
                        }}
                      />

                      {/* Vehicle Details (if not in edit mode) */}
                      {!editingVehicleId || editingVehicleId !== vehicle.id ? (
                        <CardContent sx={{ width: "100%" }}>
                          <Typography
                            variant="h6"
                            sx={{ textAlign: "center", fontWeight: "bold" }}
                          >
                            {vehicle.brand} {vehicle.model}
                          </Typography>
                          <Typography variant="body2">
                            Plate Number: {vehicle.plateNr || "N/A"}
                          </Typography>
                          <Typography variant="body2">
                            Description:{" "}
                            {vehicle.description || "No description available"}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: "bold", mt: 1 }}
                          >
                            Deposit: {vehicle.deposit || "N/A"}₱
                          </Typography>

                          {/* Rental Rates (Only show if they exist) */}
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: "bold", mt: 1 }}
                          >
                            Rental Rates:
                          </Typography>
                          {vehicle.dailyRate ? (
                            <Typography variant="body2">
                              Daily: {vehicle.dailyRate}₱
                            </Typography>
                          ) : null}
                          {vehicle.oneWeekRate ? (
                            <Typography variant="body2">
                              1 Week: {vehicle.oneWeekRate}₱
                            </Typography>
                          ) : null}
                          {vehicle.twoWeeksRate ? (
                            <Typography variant="body2">
                              2 Weeks: {vehicle.twoWeeksRate}₱
                            </Typography>
                          ) : null}
                          {vehicle.oneMonthRate ? (
                            <Typography variant="body2">
                              1 Month: {vehicle.oneMonthRate}₱
                            </Typography>
                          ) : null}

                          {/* Extras */}
                          {vehicle.extras && vehicle.extras.length > 0 && (
                            <>
                              <Typography
                                variant="body2"
                                sx={{ fontWeight: "bold", mt: 1 }}
                              >
                                Extras:
                              </Typography>
                              <List dense>
                                {vehicle.extras.map((extra, index) => (
                                  <ListItem
                                    key={index}
                                    sx={{ fontSize: "14px" }}
                                  >
                                    {typeof extra === "object"
                                      ? `${extra.name} - ${extra.price}₱`
                                      : extra}
                                  </ListItem>
                                ))}
                              </List>
                            </>
                          )}
                        </CardContent>
                      ) : (
                        // Edit Form (if in edit mode)
                        <CardContent sx={{ width: "100%" }}>
                          <TextField
                            label="Brand"
                            fullWidth
                            name="brand"
                            value={vehicleFormData.brand}
                            onChange={handleVehicleInputChange}
                            sx={{ mb: 1 }}
                          />
                          <TextField
                            label="Model"
                            fullWidth
                            name="model"
                            value={vehicleFormData.model}
                            onChange={handleVehicleInputChange}
                            sx={{ mb: 1 }}
                          />
                          <TextField
                            label="Plate Number"
                            fullWidth
                            name="plateNr"
                            value={vehicleFormData.plateNr}
                            onChange={handleVehicleInputChange}
                            sx={{ mb: 1 }}
                          />
                          <TextField
                            label="Description"
                            fullWidth
                            name="description"
                            value={vehicleFormData.description}
                            onChange={handleVehicleInputChange}
                            sx={{ mb: 1 }}
                          />
                          <TextField
                            label="Deposit"
                            fullWidth
                            name="deposit"
                            value={vehicleFormData.deposit}
                            onChange={handleVehicleInputChange}
                            sx={{ mb: 1 }}
                          />

                          {/* Rental Rates */}
                          <TextField
                            label="Daily Rate"
                            fullWidth
                            name="dailyRate"
                            value={vehicleFormData.dailyRate}
                            onChange={handleVehicleInputChange}
                            sx={{ mb: 1 }}
                          />
                          <TextField
                            label="1 Week Rate"
                            fullWidth
                            name="oneWeekRate"
                            value={vehicleFormData.oneWeekRate}
                            onChange={handleVehicleInputChange}
                            sx={{ mb: 1 }}
                          />
                          <TextField
                            label="2 Weeks Rate"
                            fullWidth
                            name="twoWeeksRate"
                            value={vehicleFormData.twoWeeksRate}
                            onChange={handleVehicleInputChange}
                            sx={{ mb: 1 }}
                          />
                          <TextField
                            label="1 Month Rate"
                            fullWidth
                            name="oneMonthRate"
                            value={vehicleFormData.oneMonthRate}
                            onChange={handleVehicleInputChange}
                            sx={{ mb: 1 }}
                          />

                          {/* Buttons */}
                          <Box display="flex" justifyContent="space-between">
                            <Button
                              onClick={handleVehicleSaveEdit}
                              variant="contained"
                              color="success"
                            >
                              Save
                            </Button>
                            <Button
                              onClick={handleVehicleCancelEdit}
                              variant="outlined"
                              color="error"
                            >
                              Cancel
                            </Button>
                          </Box>
                        </CardContent>
                      )}
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}

          {tab === 2 && (
            <Box>
              <Typography variant="h5">Bookings</Typography>

              <Grid container spacing={2}>
                {bookings
                  .filter((booking) => booking.bookingMethod === "online")
                  .map((booking) => {
                    // Calculate rental days
                    const startDate = new Date(booking.startDate);
                    const endDate = new Date(booking.endDate);
                    const rentalDays = Math.round(
                      (endDate - startDate) / (1000 * 60 * 60 * 24)
                    );

                    return (
                      <Grid item xs={12} sm={6} md={4} key={booking.id}>
                        <Card
                          sx={{
                            p: 2,
                            display: "flex",
                            flexDirection: "column",
                          }}
                        >
                          <CardContent>
                            <Typography
                              variant="h6"
                              sx={{ fontWeight: "bold" }}
                            >
                              {booking.title}
                            </Typography>
                            <Typography variant="body2">
                              <strong>Vehicle Type:</strong>{" "}
                              {booking.vehicleType || "N/A"}
                            </Typography>
                            <Typography variant="body2">
                              <strong>Start Date:</strong>{" "}
                              {startDate.toLocaleDateString()}
                            </Typography>
                            <Typography variant="body2">
                              <strong>End Date:</strong>{" "}
                              {endDate.toLocaleDateString()}
                            </Typography>
                            <Typography variant="body2">
                              <strong>Rental Days:</strong> {rentalDays}
                            </Typography>
                            <Typography variant="body2">
                              <strong>Daily Price:</strong>{" "}
                              {booking.dailyPrice || "N/A"}₱
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: "bold", mt: 1 }}
                            >
                              <strong>Total Price:</strong>{" "}
                              {booking.totalPrice || "N/A"}₱
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                    );
                  })}
              </Grid>
            </Box>
          )}

          {tab === 3 && (
            <Box>
              <Typography variant="h5">Logs</Typography>
              {/* <Select
                value={filterAction}
                onChange={(e) => setFilterAction(e.target.value)}
                sx={{ mb: 2 }}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="booking_updated">Booking Updated</MenuItem>
              </Select>
              <TextField
                type="date"
                value={filterDate}
                onChange={(e) => setFilterDate(e.target.value)}
                sx={{ mb: 2 }}
              /> */}
              {logs.map((log) => (
                <Card key={log.id} sx={{ mb: 2 }}>
                  <CardContent>
                    <Typography>
                      {log.action.replace("_", " ")} -{" "}
                      {formatDateTime(log.timestamp)}
                    </Typography>
                    {formatLogDetails(log)}
                  </CardContent>
                </Card>
              ))}
            </Box>
          )}
        </>
      )}
    </Container>
  );
};

export default ShopDetailsPage;
